// i18n: русский и английский интерфейс (21.10 — английский трейлер ведёт
// в игру зрителей, которые не читают по-русски).
//
// Ключ перевода — сама русская строка, как она написана в коде: русский текст
// остаётся в исходниках, английский лежит в словаре i18n_en.ts. Подстановки —
// {имя}: t("Нужно {price} серебра", { price }).
//
// Язык: ?lang=en|ru (проверка, съёмка трейлера; не запоминается) →
// localStorage tipaduma.lang (переключатель RU/EN в меню) →
// navigator.language (ru* → русский, иначе английский).
//
// Статичный текст index.html переводит translateDom(): обходит текстовые узлы
// и атрибуты (aria-label, title, placeholder) и меняет те, что есть в словаре.
// Тексты сюжета приходят с сервера по-русски и переводятся тем же словарём.

import { EN } from "./i18n_en";

export type Lang = "ru" | "en";

const LS_KEY = "tipaduma.lang";

function detect(): Lang {
  try {
    const q = new URLSearchParams(location.search).get("lang");
    if (q === "ru" || q === "en") return q;
  } catch { /* нет location — по умолчанию ниже */ }
  try {
    const s = localStorage.getItem(LS_KEY);
    if (s === "ru" || s === "en") return s;
  } catch { /* приватный режим */ }
  const nav = (typeof navigator !== "undefined" && navigator.language) || "";
  return nav.toLowerCase().startsWith("ru") ? "ru" : "en";
}

let lang: Lang = detect();

export function getLang(): Lang {
  return lang;
}

const missed = new Set<string>();

/** Перевод русской строки на текущий язык (нет в словаре — как есть). */
export function t(ru: string, params?: Record<string, string | number>): string {
  let s = ru;
  if (lang === "en") {
    const en = EN[ru];
    if (en !== undefined) s = en;
    else if (import.meta.env.DEV && /[А-Яа-яЁё]/.test(ru) && !missed.has(ru)) {
      missed.add(ru);
      console.warn("[i18n] нет перевода:", JSON.stringify(ru));
    }
  }
  if (params) s = s.replace(/\{(\w+)\}/g, (m, k: string) => (k in params ? String(params[k]) : m));
  return s;
}

/** Есть ли у строки перевод (для текстов с сервера: нет — берём запасной по номеру). */
export function hasT(ru: string): boolean {
  return lang !== "en" || EN[ru] !== undefined;
}

// ─── Статичный DOM ───────────────────────────────────────────────────────

const ATTRS = ["aria-label", "title", "placeholder"] as const;
/** Русский оригинал узла, который мы перевели (для обратного переключения). */
const textRu = new WeakMap<Text, string>();
const attrRu = new WeakMap<Element, Record<string, string>>();
let reverse: Map<string, string> | null = null;

/** Атрибут на корне разметки, которую код собрал уже переведённой (HUD,
 *  карточка персонажа): там английский узнаётся по обратному словарю. */
const JS_ROOT = "[data-i18n-js]";

function ruOf(cur: string, saved: string | undefined, jsBuilt: boolean): string {
  // Узел мог переписать JS: сохранённый оригинал годен, только пока текст
  // всё ещё наш перевод.
  if (saved !== undefined && (cur === saved || cur === EN[saved])) return saved;
  // Обратный словарь — только внутри JS_ROOT: статичный английский
  // index.html («Loading…», «Health») не должен русеть при переключении.
  if (!jsBuilt || EN[cur] !== undefined) return cur;
  if (!reverse) {
    reverse = new Map();
    for (const [ru, en] of Object.entries(EN)) if (!reverse.has(en)) reverse.set(en, ru);
  }
  return reverse.get(cur) ?? cur;
}

/** Перевод без предупреждения о пропуске: обход DOM видит и строки, уже
 *  собранные кодом с числами («Уровень 1: 40 серебра») — их перерисует код. */
function plain(ru: string): string {
  return lang === "en" ? EN[ru] ?? ru : ru;
}

/** Перевести текстовые узлы и атрибуты под root на текущий язык. */
export function translateDom(root: ParentNode = document.body): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode() as Text | null; n; n = walker.nextNode() as Text | null) {
    const raw = n.nodeValue ?? "";
    const cur = raw.trim();
    if (!cur || !/\p{L}/u.test(cur)) continue;
    const p = n.parentElement;
    if (p && (p.tagName === "SCRIPT" || p.tagName === "STYLE")) continue;
    const ru = ruOf(cur, textRu.get(n), lang === "ru" && !!p?.closest(JS_ROOT));
    const next = plain(ru);
    if (ru !== cur || next !== cur) textRu.set(n, ru);
    if (next !== cur) n.nodeValue = raw.replace(cur, next);
  }
  const els = (root as Element).querySelectorAll?.("[aria-label],[title],[placeholder]") ?? [];
  for (const el of Array.from(els)) {
    let saved = attrRu.get(el);
    const jsBuilt = lang === "ru" && !!el.closest(JS_ROOT);
    for (const a of ATTRS) {
      const cur = el.getAttribute(a);
      if (!cur) continue;
      const ru = ruOf(cur, saved?.[a], jsBuilt);
      const next = plain(ru);
      if (ru !== cur || next !== cur) {
        if (!saved) attrRu.set(el, (saved = {}));
        saved[a] = ru;
      }
      if (next !== cur) el.setAttribute(a, next);
    }
  }
}

// ─── Живой текст из кода ─────────────────────────────────────────────────

type Params = Record<string, string | number>;
const live = new Map<HTMLElement, { ru: string; params?: Params; out: string }>();

/** textContent = t(ru, params) и запомнить: при смене языка элемент
 *  перерисуется сам (если код с тех пор не переписал его другим текстом). */
export function setText(el: HTMLElement, ru: string, params?: Params): void {
  const out = t(ru, params);
  el.textContent = out;
  live.set(el, params ? { ru, params, out } : { ru, out });
}

function relabelLive(): void {
  for (const [el, rec] of live) {
    if (el.textContent !== rec.out) {
      live.delete(el);
      continue;
    }
    rec.out = t(rec.ru, rec.params);
    el.textContent = rec.out;
  }
}

// ─── Переключатель ───────────────────────────────────────────────────────

/** Событие на window после смены языка — повод перерисовать текст из кода. */
export const LANG_EVENT = "tipaduma:lang";

export function setLang(next: Lang): void {
  if (next === lang) return;
  lang = next;
  try {
    localStorage.setItem(LS_KEY, next);
  } catch { /* приватный режим — до перезагрузки */ }
  document.documentElement.lang = next;
  relabelLive();
  translateDom(document.body);
  syncSwitch();
  window.dispatchEvent(new Event(LANG_EVENT));
}

function syncSwitch(): void {
  for (const b of Array.from(document.querySelectorAll<HTMLButtonElement>("#langSwitch [data-lang]"))) {
    const on = b.dataset.lang === lang;
    b.classList.toggle("on", on);
    b.setAttribute("aria-pressed", on ? "true" : "false");
  }
}

/** Старт: язык документа, перевод index.html, кнопки RU/EN. */
export function initI18n(): void {
  document.documentElement.lang = lang;
  if (lang !== "ru") translateDom(document.body);
  for (const b of Array.from(document.querySelectorAll<HTMLButtonElement>("#langSwitch [data-lang]"))) {
    b.addEventListener("click", () => setLang(b.dataset.lang === "en" ? "en" : "ru"));
  }
  syncSwitch();
}
